import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Component from '../../components/Component';
import { BASE_URL } from '../../components/AppConfig';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Edit, KeyboardArrowDown, KeyboardArrowUp, Refresh } from '@mui/icons-material';

const WEEK_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const EDITABLE_DEFAULTS = {
  title: '',
  summarizer: '',
  subtitle_label: '',
  image_url: '',
  is_visible: true,
  admin_sort_order: '',
  before_topic_row_id: '',
  after_topic_row_id: '',
};

function getAuthHeaders(extraHeaders = {}) {
  const sessionToken = sessionStorage.getItem('sessionToken');
  return {
    ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
    ...extraHeaders,
  };
}

function MatchHashtagTopicsDashboard() {
  const [topics, setTopics] = useState([]);
  const [matches, setMatches] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [matchLoading, setMatchLoading] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });
  const [matchSearch, setMatchSearch] = useState('');

  const [filters, setFilters] = useState({
    match_id: '',
    date: '',
    day: '',
  });

  const [editOpen, setEditOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [editData, setEditData] = useState(EDITABLE_DEFAULTS);
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reorderingRowId, setReorderingRowId] = useState(null);

  const canGoPrevious = offset > 0;
  const canGoNext = offset + limit < total;

  const queryString = useMemo(() => {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });
    if (filters.match_id) params.append('match_id', filters.match_id);
    if (filters.date) params.append('date', filters.date);
    if (filters.day) params.append('day', filters.day);
    return params.toString();
  }, [filters, limit, offset]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchMatchOptions = useCallback(async () => {
    setMatchLoading(true);
    try {
      const params = new URLSearchParams({
        search: matchSearch,
        limit: '100',
        offset: '0',
      });

      const response = await fetch(`${BASE_URL}/admin/match-hashtag-topics/matches?${params.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result?.message || 'Failed to fetch match options');
      }
      setMatches(result?.data?.matches || []);
    } catch (fetchError) {
      showSnackbar(fetchError.message || 'Failed to fetch match options', 'error');
      setMatches([]);
    } finally {
      setMatchLoading(false);
    }
  }, [matchSearch]);

  const fetchDashboardRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${BASE_URL}/admin/match-hashtag-topics/dashboard?${queryString}`, {
        method: 'GET',
        credentials: 'include',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result?.message || 'Failed to fetch topics dashboard');
      }

      setTopics(result?.data?.topics || []);
      setTotal(result?.data?.total || 0);
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to fetch topics dashboard');
      setTopics([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  useEffect(() => {
    fetchMatchOptions();
  }, [fetchMatchOptions]);

  useEffect(() => {
    fetchDashboardRows();
  }, [fetchDashboardRows]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setOffset(0);
    fetchDashboardRows();
  };

  const openEditDialog = (row) => {
    setEditingRow(row);
    setEditData({
      title: row?.title || '',
      summarizer: row?.summarizer || '',
      subtitle_label: row?.subtitle_label || '',
      image_url: row?.image_url || '',
      is_visible: Boolean(row?.is_visible),
      admin_sort_order: row?.admin_sort_order === null || row?.admin_sort_order === undefined ? '' : String(row.admin_sort_order),
      before_topic_row_id: '',
      after_topic_row_id: '',
    });
    setImageFile(null);
    setEditOpen(true);
  };

  const closeEditDialog = () => {
    setEditOpen(false);
    setEditingRow(null);
    setEditData(EDITABLE_DEFAULTS);
    setImageFile(null);
  };

  const submitImageUpload = async (rowId) => {
    if (!imageFile) return;
    const formData = new FormData();
    formData.append('image', imageFile);
    const uploadResponse = await fetch(`${BASE_URL}/admin/match-hashtag-topics/row/${rowId}/image`, {
      method: 'PUT',
      credentials: 'include',
      headers: getAuthHeaders(),
      body: formData,
    });
    const uploadResult = await uploadResponse.json().catch(() => ({}));
    if (!uploadResponse.ok) {
      throw new Error(uploadResult?.message || 'Image upload failed');
    }
  };

  const handleSaveRow = async () => {
    if (!editingRow?.id) return;
    setSaving(true);
    try {
      const hasBeforeTarget = editData.before_topic_row_id !== '';
      const hasAfterTarget = editData.after_topic_row_id !== '';
      if (hasBeforeTarget && hasAfterTarget) {
        throw new Error('Use either "Move Before Row ID" or "Move After Row ID", not both.');
      }

      if (editData.admin_sort_order !== '') {
        const parsedSortOrder = Number(editData.admin_sort_order);
        if (!Number.isInteger(parsedSortOrder) || parsedSortOrder < 0) {
          throw new Error('Admin sort order must be a non-negative integer.');
        }
      }

      if (hasBeforeTarget) {
        const parsedBeforeId = Number(editData.before_topic_row_id);
        if (!Number.isInteger(parsedBeforeId) || parsedBeforeId <= 0) {
          throw new Error('Move Before Row ID must be a valid positive number.');
        }
      }

      if (hasAfterTarget) {
        const parsedAfterId = Number(editData.after_topic_row_id);
        if (!Number.isInteger(parsedAfterId) || parsedAfterId <= 0) {
          throw new Error('Move After Row ID must be a valid positive number.');
        }
      }

      const payload = {
        title: editData.title || null,
        summarizer: editData.summarizer || null,
        subtitle_label: editData.subtitle_label || null,
        image_url: editData.image_url || null,
        is_visible: editData.is_visible,
      };

      if (hasBeforeTarget) {
        payload.before_topic_row_id = Number(editData.before_topic_row_id);
      } else if (hasAfterTarget) {
        payload.after_topic_row_id = Number(editData.after_topic_row_id);
      } else {
        payload.admin_sort_order = editData.admin_sort_order === '' ? null : Number(editData.admin_sort_order);
      }

      const response = await fetch(`${BASE_URL}/admin/match-hashtag-topics/row/${editingRow.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result?.message || 'Failed to update topic row');
      }

      await submitImageUpload(editingRow.id);
      showSnackbar('Topic row updated successfully');
      closeEditDialog();
      fetchDashboardRows();
    } catch (saveError) {
      showSnackbar(saveError.message || 'Failed to update topic row', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleMoveRow = async (row, direction) => {
    const rowIndex = topics.findIndex((topic) => topic.id === row.id);
    if (rowIndex < 0) return;

    const targetIndex = direction === 'up' ? rowIndex - 1 : rowIndex + 1;
    if (targetIndex < 0 || targetIndex >= topics.length) return;

    const targetRow = topics[targetIndex];
    const payload =
      direction === 'up'
        ? { before_topic_row_id: targetRow.id }
        : { after_topic_row_id: targetRow.id };

    setReorderingRowId(row.id);
    try {
      const response = await fetch(`${BASE_URL}/admin/match-hashtag-topics/row/${row.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result?.message || 'Failed to reorder topic row');
      }
      showSnackbar('Topic order updated');
      fetchDashboardRows();
    } catch (moveError) {
      showSnackbar(moveError.message || 'Failed to reorder topic row', 'error');
    } finally {
      setReorderingRowId(null);
    }
  };

  const handleQuickVisibilityToggle = async (row) => {
    try {
      const response = await fetch(`${BASE_URL}/admin/match-hashtag-topics/row/${row.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ is_visible: !row.is_visible }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result?.message || 'Failed to update visibility');
      }
      showSnackbar('Visibility updated');
      fetchDashboardRows();
    } catch (toggleError) {
      showSnackbar(toggleError.message || 'Failed to update visibility', 'error');
    }
  };

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <h3>Match Hashtag Topics Dashboard</h3>
              </div>
            </div>
          </div>
        </div>

        <div className="container-fluid">
          <div className="row">
            <div className="col-sm-12">
              <Card>
                <CardContent>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Search Match"
                        size="small"
                        fullWidth
                        value={matchSearch}
                        onChange={(event) => setMatchSearch(event.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={fetchMatchOptions}
                        startIcon={matchLoading ? <CircularProgress size={16} /> : <Refresh />}
                        disabled={matchLoading}
                        sx={{ height: '40px' }}
                      >
                        Refresh Matches
                      </Button>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        label="Match"
                        name="match_id"
                        value={filters.match_id}
                        onChange={handleFilterChange}
                      >
                        <MenuItem value="">All matches</MenuItem>
                        {matches.map((match) => (
                          <MenuItem key={match.match_id || match.id} value={String(match.match_id || match.id)}>
                            {match.match_id || match.id} - {match.title || match.short_title || 'Match'}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        label="Day"
                        name="day"
                        value={filters.day}
                        onChange={handleFilterChange}
                      >
                        <MenuItem value="">All days</MenuItem>
                        {WEEK_DAYS.map((day) => (
                          <MenuItem key={day} value={day}>
                            {day}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Date"
                        name="date"
                        type="date"
                        size="small"
                        fullWidth
                        value={filters.date}
                        onChange={handleFilterChange}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Button variant="contained" fullWidth onClick={handleApplyFilters} sx={{ height: '40px' }}>
                        Apply Filters
                      </Button>
                    </Grid>
                  </Grid>

                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}

                  {filters.match_id && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Rows are shown in app story order for this match. Use move up/down or edit to change order.
                    </Alert>
                  )}

                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                              <TableCell><strong>Topic ID</strong></TableCell>
                            <TableCell><strong>Match ID</strong></TableCell>
                            <TableCell><strong>Topic Key</strong></TableCell>
                            <TableCell><strong>Scope</strong></TableCell>
                            <TableCell><strong>Title</strong></TableCell>
                            <TableCell><strong>Summary</strong></TableCell>
                            <TableCell><strong>Visible</strong></TableCell>
                            <TableCell><strong>Sort Order</strong></TableCell>
                            {['54', '55'].includes(String(filters.match_id)) && (
                              <TableCell><strong>Momentum Order</strong></TableCell>
                            )}
                            <TableCell><strong>Score</strong></TableCell>
                            <TableCell><strong>Posts</strong></TableCell>
                            <TableCell><strong>Updated At</strong></TableCell>
                            <TableCell><strong>Actions</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {topics.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={['54', '55'].includes(String(filters.match_id)) ? 13 : 12} align="center">
                                No topics found
                              </TableCell>
                            </TableRow>
                          ) : (
                            topics.map((row, rowIndex) => (
                              <TableRow key={row.id}>
                                <TableCell>{row.id}</TableCell>
                                <TableCell>{row.match_id}</TableCell>
                                <TableCell>{row.topic_key || '-'}</TableCell>
                                <TableCell>{row.scope || '-'}</TableCell>
                                <TableCell>{row.title || '-'}</TableCell>
                                <TableCell>{row.summarizer || '-'}</TableCell>
                                <TableCell>
                                  <FormControlLabel
                                    control={<Switch checked={Boolean(row.is_visible)} onChange={() => handleQuickVisibilityToggle(row)} />}
                                    label={row.is_visible ? 'Yes' : 'No'}
                                  />
                                </TableCell>
                                <TableCell>{row.admin_sort_order ?? '-'}</TableCell>
                                {['54', '55'].includes(String(filters.match_id)) && (
                                  <TableCell>{row.momentum_sort_order ?? '-'}</TableCell>
                                )}
                                <TableCell>{row.score ?? '-'}</TableCell>
                                <TableCell>{row.post_count ?? '-'}</TableCell>
                                <TableCell>{row.updated_at ? new Date(row.updated_at).toLocaleString() : '-'}</TableCell>
                                <TableCell>
                                  {filters.match_id && (
                                    <>
                                      <IconButton
                                        color="primary"
                                        onClick={() => handleMoveRow(row, 'up')}
                                        size="small"
                                        disabled={rowIndex === 0 || reorderingRowId === row.id}
                                        title="Move up in app order"
                                      >
                                        <KeyboardArrowUp />
                                      </IconButton>
                                      <IconButton
                                        color="primary"
                                        onClick={() => handleMoveRow(row, 'down')}
                                        size="small"
                                        disabled={rowIndex === topics.length - 1 || reorderingRowId === row.id}
                                        title="Move down in app order"
                                      >
                                        <KeyboardArrowDown />
                                      </IconButton>
                                    </>
                                  )}
                                  <IconButton color="primary" onClick={() => openEditDialog(row)} size="small">
                                    <Edit />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Button variant="outlined" onClick={() => setOffset((prev) => Math.max(0, prev - limit))} disabled={!canGoPrevious || loading}>
                      Previous
                    </Button>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                      Total: {total} | Offset: {offset} | Limit: {limit}
                    </Typography>
                    <Button variant="outlined" onClick={() => setOffset((prev) => prev + limit)} disabled={!canGoNext || loading}>
                      Next
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={editOpen} onClose={closeEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Topic Row</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={editData.title}
                onChange={(event) => setEditData((prev) => ({ ...prev, title: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Summary"
                multiline
                minRows={3}
                value={editData.summarizer}
                onChange={(event) => setEditData((prev) => ({ ...prev, summarizer: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subtitle Label"
                value={editData.subtitle_label}
                onChange={(event) => setEditData((prev) => ({ ...prev, subtitle_label: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Image URL"
                value={editData.image_url}
                onChange={(event) => setEditData((prev) => ({ ...prev, image_url: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <Button component="label" variant="outlined" fullWidth>
                {imageFile ? imageFile.name : 'Upload image file (optional)'}
                <input hidden type="file" accept="image/*" onChange={(event) => setImageFile(event.target.files?.[0] || null)} />
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Admin Sort Order"
                type="number"
                value={editData.admin_sort_order}
                onChange={(event) => setEditData((prev) => ({ ...prev, admin_sort_order: event.target.value }))}
                disabled={editData.before_topic_row_id !== '' || editData.after_topic_row_id !== ''}
                helperText="Fallback list position. Leave empty to send to end."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Move Before Row ID"
                type="number"
                value={editData.before_topic_row_id}
                onChange={(event) => setEditData((prev) => ({ ...prev, before_topic_row_id: event.target.value }))}
                disabled={editData.after_topic_row_id !== ''}
                helperText="Optional relative move target."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Move After Row ID"
                type="number"
                value={editData.after_topic_row_id}
                onChange={(event) => setEditData((prev) => ({ ...prev, after_topic_row_id: event.target.value }))}
                disabled={editData.before_topic_row_id !== ''}
                helperText="Optional relative move target."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editData.is_visible}
                    onChange={(event) => setEditData((prev) => ({ ...prev, is_visible: event.target.checked }))}
                  />
                }
                label={editData.is_visible ? 'Visible' : 'Hidden'}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveRow} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4500} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Component>
  );
}

export default MatchHashtagTopicsDashboard;
